import React, { useState, useEffect } from 'react';
import { timeEntriesApi, payPeriodsApi, adminApi, formatDate, formatDateRange } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { 
  CheckCircle2, 
  XCircle, 
  Clock,
  Pencil,
  Trash2,
  FileText
} from 'lucide-react';
import { toast } from 'sonner';

const MyEntries = () => {
  const { impersonating } = useAuth();
  const [payPeriods, setPayPeriods] = useState([]);
  const [selectedPeriod, setSelectedPeriod] = useState('');
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Edit dialog state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [editHours, setEditHours] = useState('');
  const [editProject, setEditProject] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [saving, setSaving] = useState(false);

  // Delete dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingEntry, setDeletingEntry] = useState(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchPayPeriods();
  }, []);

  useEffect(() => {
    if (selectedPeriod) {
      fetchEntries();
    }
  }, [selectedPeriod]);

  const fetchPayPeriods = async () => {
    try {
      const response = await payPeriodsApi.getAll();
      setPayPeriods(response.data);
      const current = response.data.find(p => p.is_current);
      if (current) {
        setSelectedPeriod(current.start_date);
      }
    } catch (error) {
      toast.error('Failed to load pay periods');
    }
  };

  const fetchEntries = async () => {
    setLoading(true);
    try {
      const response = impersonating 
        ? await adminApi.getTimeEntries({ payPeriodStart: selectedPeriod, userId: impersonating.id })
        : await timeEntriesApi.getAll(selectedPeriod);
      setEntries(response.data);
    } catch (error) {
      toast.error('Failed to load entries');
    } finally {
      setLoading(false);
    }
  };

  const openEditDialog = (entry) => {
    setEditingEntry(entry);
    setEditHours(entry.hours.toString());
    setEditProject(entry.project);
    setEditDescription(entry.description);
    setEditDialogOpen(true);
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      await timeEntriesApi.update(editingEntry.id, {
        hours: parseFloat(editHours),
        project: editProject,
        description: editDescription
      });
      toast.success('Entry updated');
      setEditDialogOpen(false);
      fetchEntries();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to update entry';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const openDeleteDialog = (entry) => {
    setDeletingEntry(entry);
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await timeEntriesApi.delete(deletingEntry.id);
      toast.success('Entry deleted');
      setDeleteDialogOpen(false);
      fetchEntries();
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to delete entry';
      toast.error(message);
    } finally {
      setDeleting(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return (
          <Badge variant="outline" className="bg-green-100 text-green-800 border-green-200 text-xs">
            <CheckCircle2 className="w-3 h-3 mr-1" aria-hidden="true" />
            Approved
          </Badge>
        );
      case 'rejected':
        return (
          <Badge variant="outline" className="bg-red-100 text-red-800 border-red-200 text-xs">
            <XCircle className="w-3 h-3 mr-1" aria-hidden="true" />
            Rejected
          </Badge>
        );
      default:
        return (
          <Badge variant="outline" className="bg-amber-100 text-amber-800 border-amber-200 text-xs">
            <Clock className="w-3 h-3 mr-1" aria-hidden="true" />
            Pending
          </Badge>
        );
    }
  };

  // Stats
  const totalHours = entries.reduce((sum, e) => sum + e.hours, 0);
  const approvedHours = entries.filter(e => e.status === 'approved').reduce((sum, e) => sum + e.hours, 0);
  const pendingHours = entries.filter(e => e.status === 'pending').reduce((sum, e) => sum + e.hours, 0);

  return (
    <Layout>
      <div className="space-y-6" data-testid="my-entries-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl md:text-3xl font-bold tracking-tight text-slate-900">
              My Time Entries
            </h1>
            <p className="text-slate-500 text-sm mt-1">
              View and manage your submitted hours
            </p>
          </div>

          <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
            <SelectTrigger className="w-full sm:w-64 border-slate-300" data-testid="period-select">
              <SelectValue placeholder="Select pay period" />
            </SelectTrigger>
            <SelectContent>
              {payPeriods.map((period) => (
                <SelectItem 
                  key={period.start_date} 
                  value={period.start_date}
                >
                  {formatDateRange(period.start_date, period.end_date)}
                  {period.is_current && ' (Current)'}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-4">
          <Card className="bg-white border border-slate-200 shadow-sm">
            <CardContent className="p-5">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Total</p>
              <p className="text-2xl font-bold text-slate-900 mt-1" data-testid="my-total-hours">
                {totalHours.toFixed(1)} <span className="text-base text-slate-500 font-normal">hrs</span>
              </p>
            </CardContent>
          </Card>
          <Card className="bg-white border border-slate-200 shadow-sm">
            <CardContent className="p-5">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Approved</p>
              <p className="text-2xl font-bold text-green-600 mt-1" data-testid="my-approved-hours">
                {approvedHours.toFixed(1)} <span className="text-base text-slate-500 font-normal">hrs</span>
              </p>
            </CardContent>
          </Card>
          <Card className="bg-white border border-slate-200 shadow-sm">
            <CardContent className="p-5">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Pending</p>
              <p className="text-2xl font-bold text-amber-600 mt-1" data-testid="my-pending-hours">
                {pendingHours.toFixed(1)} <span className="text-base text-slate-500 font-normal">hrs</span>
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Entries List */}
        <Card className="bg-white border border-slate-200 shadow-sm">
          <CardHeader className="border-b border-slate-200">
            <CardTitle className="font-heading text-lg">
              Entries for {selectedPeriod && formatDateRange(
                selectedPeriod, 
                payPeriods.find(p => p.start_date === selectedPeriod)?.end_date || ''
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-pulse text-slate-500">Loading...</div>
              </div>
            ) : entries.length === 0 ? (
              <div className="text-center py-12">
                <FileText className="w-12 h-12 text-slate-300 mx-auto mb-4" aria-hidden="true" />
                <p className="text-slate-500">
                  No entries for this pay period.
                </p>
              </div>
            ) : (
              <div className="divide-y divide-slate-100">
                {entries.map((entry) => (
                  <div
                    key={entry.id}
                    className="flex items-center justify-between p-4 hover:bg-slate-50 transition-colors"
                    data-testid={`entry-row-${entry.id}`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 mb-1">
                        <span className="font-mono text-sm text-brick-cyan">
                          {formatDate(entry.date)}
                        </span>
                        {getStatusBadge(entry.status)}
                      </div>
                      <p className="font-medium text-slate-900 truncate">{entry.project}</p>
                      <p className="text-sm text-slate-500 truncate">
                        {entry.description}
                      </p>
                    </div>
                    <div className="flex items-center gap-4 ml-4">
                      <div className="text-right">
                        <span className="text-xl font-bold text-slate-900">
                          {entry.hours}
                        </span>
                        <span className="text-sm text-slate-500 ml-1">hrs</span>
                      </div>
                      {entry.status === 'pending' && !impersonating && (
                        <div className="flex gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openEditDialog(entry)}
                            data-testid={`edit-entry-${entry.id}`}
                            className="hover:bg-brick-cyan/10 hover:text-brick-cyan"
                          >
                            <Pencil className="w-4 h-4" aria-hidden="true" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openDeleteDialog(entry)}
                            data-testid={`delete-entry-${entry.id}`}
                            className="hover:bg-red-50"
                          >
                            <Trash2 className="w-4 h-4 text-red-500" aria-hidden="true" />
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Edit Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">Edit Entry</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Hours</Label>
                <Input
                  type="number"
                  step="0.5"
                  min="0"
                  max="24"
                  value={editHours}
                  onChange={(e) => setEditHours(e.target.value)}
                  data-testid="edit-hours-input"
                  className="border-slate-300 focus:border-brick-cyan focus:ring-1 focus:ring-brick-cyan"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Project</Label>
                <Input
                  value={editProject}
                  onChange={(e) => setEditProject(e.target.value)}
                  data-testid="edit-project-input"
                  className="border-slate-300 focus:border-brick-cyan focus:ring-1 focus:ring-brick-cyan"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-wide text-slate-500">Description</Label>
                <Textarea
                  value={editDescription}
                  onChange={(e) => setEditDescription(e.target.value)}
                  className="resize-none border-slate-300 focus:border-brick-cyan focus:ring-1 focus:ring-brick-cyan"
                  rows={3}
                  data-testid="edit-description-input"
                />
              </div>
            </div>
            <DialogFooter className="mt-6">
              <Button variant="outline" onClick={() => setEditDialogOpen(false)} className="border-slate-300">
                Cancel
              </Button>
              <Button 
                onClick={handleSaveEdit} 
                disabled={saving}
                data-testid="save-edit-btn"
                className="bg-brick-red hover:bg-[#c4151d]"
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-heading text-xl">Delete Entry</DialogTitle>
            </DialogHeader>
            <p className="text-slate-500">
              Are you sure you want to delete this entry? This action cannot be undone.
            </p>
            <DialogFooter className="mt-6">
              <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} className="border-slate-300">
                Cancel
              </Button>
              <Button 
                variant="destructive" 
                onClick={handleDelete}
                disabled={deleting}
                data-testid="confirm-delete-btn"
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default MyEntries;
